import type { Metrics, Rect } from "../interfaces/types";

export const isCvReady = (): boolean =>
  typeof cv !== "undefined" && !!cv.Mat;

const MAX_ANALYSIS_DIM = 480;

const readRoi = (canvas: HTMLCanvasElement, guideRect: Rect) => {
  const { x, y, width, height } = guideRect;
  const scale = Math.min(1, MAX_ANALYSIS_DIM / Math.max(width, height));
  const dw = Math.max(1, Math.round(width * scale));
  const dh = Math.max(1, Math.round(height * scale));
  const tmp = document.createElement("canvas");
  tmp.width = dw;
  tmp.height = dh;
  const tctx = tmp.getContext("2d");
  if (!tctx) throw new Error("No se pudo obtener el contexto 2D del canvas");
  tctx.drawImage(canvas, x, y, width, height, 0, 0, dw, dh);
  const imageData = tctx.getImageData(0, 0, dw, dh);
  return cv.matFromImageData(imageData);
};

const sharpnessVariance = (gray: unknown): number => {
  const lap = new cv.Mat();
  cv.Laplacian(gray, lap, cv.CV_64F);
  const mean = new cv.Mat();
  const stddev = new cv.Mat();
  cv.meanStdDev(lap, mean, stddev);
  const sd = stddev.doubleAt(0, 0);
  lap.delete();
  mean.delete();
  stddev.delete();
  return sd * sd;
};

const brightnessMean = (gray: unknown): number => {
  const m = cv.mean(gray);
  return m[0];
};

const glareRatio = (gray: { rows: number; cols: number }): number => {
  const thresh = new cv.Mat();
  cv.threshold(gray, thresh, 250, 255, cv.THRESH_BINARY);
  const saturated = cv.countNonZero(thresh);
  const total = gray.rows * gray.cols;
  thresh.delete();
  return total ? saturated / total : 0;
};

const CARD_ASPECT = 1.585;
const CARD_ASPECT_BAND = 0.85;

const findDocumentQuad = (
  gray: unknown,
  roiArea: number,
): { areaRatio: number; aspectRatio: number } | null => {
  const blurred = new cv.Mat();
  cv.GaussianBlur(gray, blurred, new cv.Size(5, 5), 0);
  const edges = new cv.Mat();
  cv.Canny(blurred, edges, 40, 120);
  const kernel = cv.getStructuringElement(cv.MORPH_RECT, new cv.Size(5, 5));
  cv.dilate(edges, edges, kernel);
  const contours = new cv.MatVector();
  const hierarchy = new cv.Mat();
  cv.findContours(edges, contours, hierarchy, cv.RETR_EXTERNAL, cv.CHAIN_APPROX_SIMPLE);

  let best: { areaRatio: number; aspect: number; score: number } | null = null;
  for (let i = 0; i < contours.size(); i++) {
    const cnt = contours.get(i);
    const area = cv.contourArea(cnt);
    if (area >= roiArea * 0.03) {
      const rr = cv.minAreaRect(cnt);
      const w = rr.size.width;
      const h = rr.size.height;
      const long = Math.max(w, h);
      const short = Math.min(w, h);
      const aspect = short > 0 ? long / short : 0;
      const areaRatio = (w * h) / roiArea;
      const cardLike = Math.abs(aspect - CARD_ASPECT) <= CARD_ASPECT_BAND;
      const score = (cardLike ? 1 : 0) + areaRatio;
      if (!best || score > best.score) best = { areaRatio, aspect, score };
    }
    cnt.delete();
  }

  blurred.delete();
  edges.delete();
  kernel.delete();
  contours.delete();
  hierarchy.delete();

  if (!best) return null;
  return { areaRatio: best.areaRatio, aspectRatio: best.aspect };
};

export const computeMetrics = (
  canvas: HTMLCanvasElement,
  guideRect: Rect,
): Metrics => {
  const src = readRoi(canvas, guideRect);
  const gray = new cv.Mat();
  cv.cvtColor(src, gray, cv.COLOR_RGBA2GRAY);

  const roiArea = gray.rows * gray.cols;
  const quad = findDocumentQuad(gray, roiArea);
  const metrics: Metrics = {
    quadAreaRatio: quad ? quad.areaRatio : null,
    aspectRatio: quad ? quad.aspectRatio : null,
    sharpnessVariance: sharpnessVariance(gray),
    brightness: brightnessMean(gray),
    glareRatio: glareRatio(gray),
  };

  src.delete();
  gray.delete();
  return metrics;
};
